import { useEffect, useState, type CSSProperties } from 'react';
import yaml from 'js-yaml';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-yaml';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { apiClient } from '../../lib/api-client';
import { YamlSideBySideDiff } from './yaml-side-by-side-diff';

const EDITABLE_EDITOR_STYLE: CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 12,
  backgroundColor: '#1e293b',
  color: '#e2e8f0',
  minHeight: '280px',
};

export interface ApplyStreamCompareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  streamId: string;
  /** Draft YAML for this stream (initial right pane). */
  draftYaml: string;
  onApplied: (finalYaml: string) => void;
}

export function ApplyStreamCompareDialog({
  open,
  onOpenChange,
  streamId,
  draftYaml,
  onApplied,
}: ApplyStreamCompareDialogProps) {
  const [existingYaml, setExistingYaml] = useState('');
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [editedYaml, setEditedYaml] = useState(draftYaml);
  const [parseError, setParseError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showRawEditor, setShowRawEditor] = useState(false);

  useEffect(() => {
    if (!open || !streamId) return;
    setEditedYaml(draftYaml);
    setParseError(null);
    setLoadError(null);
    setShowRawEditor(false);
    setLoadingExisting(true);
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getAiConfigStreamYaml(streamId);
        if (!cancelled) {
          setExistingYaml(res.yaml);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setExistingYaml('');
          setLoadError(err instanceof Error ? err.message : 'Could not load current stream YAML');
        }
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, streamId, draftYaml]);

  const handleEditedChange = (v: string) => {
    setEditedYaml(v);
    try {
      yaml.load(v);
      setParseError(null);
    } catch (e: any) {
      setParseError(e.message);
    }
  };

  const handleApply = async () => {
    let parsed: any;
    try {
      parsed = yaml.load(editedYaml);
    } catch (e: any) {
      setParseError(e.message);
      return;
    }
    const root = parsed?.stream || parsed || {};
    const yamlId = String(root.id || root.streamId || '').trim();
    if (yamlId && yamlId !== streamId) {
      setParseError(`Stream id in YAML ("${yamlId}") must match "${streamId}".`);
      return;
    }
    setSaving(true);
    try {
      await apiClient.updateAiConfigStream(streamId, editedYaml);
      onApplied(editedYaml);
      onOpenChange(false);
    } catch (err: unknown) {
      setParseError(err instanceof Error ? err.message : 'Failed to save stream');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent maxWidth="6xl" className="max-h-[92vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            Apply stream: <span className="font-mono text-violet-600 dark:text-violet-400">{streamId}</span>
          </DialogTitle>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Split diff: removed lines on the left (red), added on the right (green). One scroll keeps both panes aligned.
            Expand &quot;Edit draft YAML&quot; to change what will be saved.
          </p>
        </DialogHeader>
        <DialogBody className="flex-1 min-h-0 flex flex-col gap-2 overflow-hidden">
          {loadError && (
            <p className="text-xs text-amber-600 dark:text-amber-400 shrink-0">
              {loadError} — compare may be incomplete.
            </p>
          )}
          <div className="flex-1 min-h-0 flex flex-col border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="text-xs font-medium px-2 py-1.5 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shrink-0 flex items-center gap-2">
              Compare: repository vs draft
              {loadingExisting && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
            </div>
            <YamlSideBySideDiff
              before={loadingExisting ? '' : existingYaml}
              after={editedYaml}
              className="flex-1 min-h-0 max-h-[52vh]"
            />
          </div>

          <button
            type="button"
            className="flex items-center gap-1 text-xs font-medium text-violet-600 dark:text-violet-400 shrink-0"
            onClick={() => setShowRawEditor((v) => !v)}
          >
            {showRawEditor ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            Edit draft YAML (this is what gets saved)
          </button>
          {showRawEditor && (
            <div className="border-2 border-violet-300 dark:border-violet-700 rounded-lg overflow-hidden shrink-0">
              <div className="text-xs font-medium px-2 py-1.5 bg-violet-50 dark:bg-violet-900/30 border-b border-violet-200 dark:border-violet-800">
                Draft — editable
              </div>
              <div className="max-h-[38vh] overflow-auto">
                <Editor
                  value={editedYaml}
                  onValueChange={handleEditedChange}
                  highlight={(code) => highlight(code, languages.yaml, 'yaml')}
                  padding={12}
                  style={EDITABLE_EDITOR_STYLE}
                  className="outline-none"
                />
              </div>
            </div>
          )}
          {parseError && (
            <p className="text-sm text-red-600 dark:text-red-400 shrink-0">Invalid YAML: {parseError}</p>
          )}
        </DialogBody>
        <DialogFooter className="shrink-0 gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleApply()} disabled={saving || !!parseError}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Apply to repository
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
